import { useRefs } from 'contexts/RefContext';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import Konva from 'konva';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-konva';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { addFlattenDraftPoint, FlattenArea, setFlattenPolygon } from 'store/slices/TreeSlice';
import { selectIsFlattenAreaMode, selectFlattenShape } from 'store/slices/TreeSlice/selector';
import {
	POLYGON_DASH,
	POLYGON_FILL_ACTIVE,
	POLYGON_STROKE,
	POLYGON_STROKE_WIDTH,
} from './FlattenAreaPolygon';
import {
	circleToPolygonPoints,
	getCircleHandlePoints,
	getImagePointFromEvent,
	shouldBlockNewShape,
	useFlattenAreaGuardRefs,
	useLatestRef,
	usePinchAwareTap,
} from './flattenAreaToolUtils';
import PolygonHandle from './PolygonHandle';
import { pointsToPolygon } from 'components/tool/utils';

export default function FlattenAreaCircleTool() {
	const dispatch = useDispatch();
	const { konvaRef } = useRefs();
	const baseImageScale = useKonvaImageScale();

	const { treeEditingMode } = useSelector((state: RootState) => state.treeSlice.data);
	const { moveStage, rotateStage, editModeEnabled, threeDModelEnabled } = useSelector((state: RootState) => state.toolNewPostions.data);
	const isFlattenAreaMode = useSelector(selectIsFlattenAreaMode);
	const flattenShape = useSelector(selectFlattenShape);
	const draftPoints = useSelector((state: RootState) => state.treeSlice.data.draftFlatten.points);
	const { selectedShapeIdRef, flattenAreaPolygonsRef, loadingFlattenIdsRef } = useFlattenAreaGuardRefs();

	const [center, setCenter] = useState<{ x: number; y: number } | null>(null);
	const [radius, setRadius] = useState(0);
	const drawingIdRef = useRef<string | null>(null);
	const drawingRef = useRef(false);

	const canEdit = editModeEnabled && treeEditingMode && isFlattenAreaMode && !threeDModelEnabled && flattenShape === 'circle';
	const canDraw = canEdit && !moveStage && !rotateStage;

	// True until the first point reaches Redux — stops the sync effect below from resetting mid-draw.
	const pendingFirstPointRef = useRef(false);

	const centerRef = useLatestRef(center);
	const radiusRef = useLatestRef(radius);

	const getImagePoint = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => (
		getImagePointFromEvent(e, baseImageScale)
	), [baseImageScale]);

	const resetLocal = useCallback(() => {
		drawingRef.current = false;
		drawingIdRef.current = null;
		pendingFirstPointRef.current = false;
		setCenter(null);
		setRadius(0);
	}, []);

	const commit = useCallback((nextRadius: number) => {
		const c = centerRef.current;
		if (!c || !drawingIdRef.current) return;
		if (nextRadius < 2) return;
		const points = circleToPolygonPoints(c.x, c.y, nextRadius);
		const id = drawingIdRef.current;
		const polygonCoords = pointsToPolygon(points);
		const flattenAreaPolygon: FlattenArea = {
			coords: polygonCoords,
			id,
			flat_px: null,
			facet_affected_px: null,
			ground_z: 0,
			height: 0,
			shape: 'circle',
		};
		dispatch(setFlattenPolygon(flattenAreaPolygon));
		resetLocal();
	}, [dispatch, resetLocal, centerRef]);

	// The actual tap action: complete on a second click, else start a new circle.
	const performTap = useCallback((point: { x: number; y: number }) => {
		const c = centerRef.current;
		if (drawingRef.current && c && drawingIdRef.current) {
			let nextRadius = radiusRef.current;
			if (nextRadius <= 0) nextRadius = Math.hypot(point.x - c.x, point.y - c.y);
			commit(nextRadius);
			return;
		}

		if (shouldBlockNewShape(
			dispatch, point, selectedShapeIdRef.current, flattenAreaPolygonsRef.current, loadingFlattenIdsRef.current
		)) return;

		drawingRef.current = true;
		drawingIdRef.current = crypto.randomUUID();
		setCenter(point);
		setRadius(0);
		pendingFirstPointRef.current = true;
		dispatch(addFlattenDraftPoint({ point }));
	}, [commit, dispatch, selectedShapeIdRef, centerRef, radiusRef, flattenAreaPolygonsRef, loadingFlattenIdsRef]);

	const {
		isPinchingRef, isConcurrentFinger, startTap, clearPointerUp,
		handleTouchStart, handleTouchMove, handleTouchEnd,
	} = usePinchAwareTap(performTap);

	const handlePointerDown = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => {
		if (!canDraw) return;
		if (isConcurrentFinger(e)) return; // 2nd, concurrent finger — circle stays exactly as is
		const point = getImagePoint(e);
		if (!point) return;
		startTap(e, point);
	}, [canDraw, getImagePoint, isConcurrentFinger, startTap]);

	const handlePointerMove = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => {
		if (!canDraw) return;
		const c = centerRef.current;
		if (!drawingRef.current || !c) return;
		if (isPinchingRef.current) return; // pinch — ignore its move noise
		const point = getImagePoint(e);
		if (!point) return;
		setRadius(Math.hypot(point.x - c.x, point.y - c.y));
	}, [canDraw, getImagePoint, centerRef, isPinchingRef]);

	const handlePointerUp = useCallback((e: Konva.KonvaEventObject<PointerEvent>) => {
		clearPointerUp(e);
		if (!canDraw) return;
		if (!drawingRef.current) return;
		if (isPinchingRef.current) return; // pinch — don't finalize
		const r = radiusRef.current;
		if (r > 0) commit(r); // drag-release completes; click-release (radius 0) does not
	}, [canDraw, commit, radiusRef, clearPointerUp, isPinchingRef]);

	useEffect(() => {
		const stage = konvaRef?.current;
		if (!stage || !canDraw) return;

		stage.on('pointerdown', handlePointerDown);
		stage.on('pointermove', handlePointerMove);
		stage.on('pointerup', handlePointerUp);
		stage.on('touchstart', handleTouchStart);
		stage.on('touchmove', handleTouchMove);
		stage.on('touchend', handleTouchEnd);
		stage.on('touchcancel', handleTouchEnd);

		return () => {
			stage.off('pointerdown', handlePointerDown);
			stage.off('pointermove', handlePointerMove);
			stage.off('pointerup', handlePointerUp);
			stage.off('touchstart', handleTouchStart);
			stage.off('touchmove', handleTouchMove);
			stage.off('touchend', handleTouchEnd);
			stage.off('touchcancel', handleTouchEnd);
		};
	}, [
		canDraw, handlePointerDown, handlePointerMove, handlePointerUp,
		handleTouchStart, handleTouchMove, handleTouchEnd, konvaRef,
	]);

	useEffect(() => {
		if (canEdit) return;
		resetLocal();
	}, [canEdit, resetLocal]);

	useEffect(() => {
		// Keep local "in-progress" circle in sync with draftFlatten so undo/redo works.
		if (!canEdit) return;

		if (draftPoints.length < 2) {
			if (drawingRef.current && !pendingFirstPointRef.current) resetLocal(); // skip while first point in flight
			return;
		}
		pendingFirstPointRef.current = false;

		const cx = draftPoints[0];
		const cy = draftPoints[1];
		if (!drawingRef.current) {
			drawingRef.current = true;
			drawingIdRef.current = crypto.randomUUID();
			setCenter({ x: cx, y: cy });
			setRadius(0);
			return;
		}

		if (!center) setCenter({ x: cx, y: cy });
	}, [canEdit, center, draftPoints, resetLocal]);

	useEffect(() => () => resetLocal(), [resetLocal]);

	const previewPoints = useMemo(() => {
		if (!center || radius <= 0) return null;
		return circleToPolygonPoints(center.x, center.y, radius);
	}, [center, radius]);

	if (!canEdit) return null;
	if (!center || !previewPoints) return null;

	return (
		<>
			<Line
				points={previewPoints}
				stroke={POLYGON_STROKE}
				strokeWidth={POLYGON_STROKE_WIDTH}
				dash={POLYGON_DASH}
				strokeScaleEnabled={false}
				closed={true}
				fill={POLYGON_FILL_ACTIVE}
				listening={false}
			/>
			{radius > 0 && getCircleHandlePoints(center.x, center.y, radius).map((p, idx) => (
				<PolygonHandle
					key={`circle-preview-handle-${idx}`}
					x={p.x}
					y={p.y}
					listening={false}
				/>
			))}
		</>
	);
}
